// src/components/CalculateurKilometrique.js
// Saisie d'une tournée multi-étapes (frais kilométriques) — version PWA mobile.

import React, { useState, useEffect, useMemo } from 'react';
import apiService, { normalizePuissanceFiscaleKey } from '../services/apiService';
import cloneManager from '../services/cloneManager';

// Barèmes 2026 officiels (fallback si l'API n'en retourne pas)
const BAREMES_2026 = {
  '3cv': { t1: 0.549, t2: 0.328, t3: 0.384 },
  '4cv': { t1: 0.627, t2: 0.352, t3: 0.423 },
  '5cv': { t1: 0.658, t2: 0.370, t3: 0.443 },
  '6cv': { t1: 0.687, t2: 0.387, t3: 0.463 },
  '7cv_et_plus': { t1: 0.720, t2: 0.407, t3: 0.487 },
};

// Adresse de secours si ni l'inspecteur ni l'API n'en fournissent
const ADRESSE_SIEGE_FALLBACK = '7 rue Ambroise Croizat, 87920 Condat sur Vienne';

const todayIso = () => new Date().toISOString().split('T')[0];

function normalizeClientFromApi(c) {
  if (!c) return c;
  return {
    ...c,
    raison_sociale: c.raison_sociale ?? c.raisonSociale ?? '',
    code_postal: c.code_postal ?? c.codePostal ?? '',
  };
}

function normalizeEtablissementFromApi(et) {
  if (!et) return et;
  return {
    ...et,
    code_postal: et.code_postal ?? et.codePostal ?? '',
    client_id: et.client_id ?? et.clientId,
  };
}

function destinationFromClient(client) {
  if (!client) return '';
  const adr = client.adresse ?? '';
  const cp = client.code_postal ?? client.codePostal ?? '';
  const ville = client.ville ?? '';
  return `${adr} ${cp} ${ville}`.trim().replace(/\s+/g, ' ');
}

function destinationFromEtablissement(et) {
  if (!et) return '';
  const adr = et.adresse ?? '';
  const cp = et.code_postal ?? et.codePostal ?? '';
  const ville = et.ville ?? '';
  const built = `${adr} ${cp} ${ville}`.trim().replace(/\s+/g, ' ');
  if (built) return built;
  return (et.adresse_complete || '').trim();
}

function clientDisplayName(client) {
  if (!client) return '';
  return (
    client.raison_sociale ||
    client.entreprise ||
    `${client.nom || ''} ${client.prenom || ''}`.trim() ||
    client.nom ||
    ''
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
    paddingBottom: '120px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: 'white',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerTitle: { fontSize: '18px', fontWeight: '700', margin: 0 },
  backBtn: {
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  content: { padding: '16px' },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '16px',
    marginBottom: '14px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
  },
  cardTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#1e40af',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  vehiculeCard: {
    background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
    border: '2px solid #3b82f6',
    borderRadius: '12px',
    padding: '12px 14px',
    marginBottom: '14px',
    fontSize: '13px',
    color: '#1e3a8a',
  },
  vehiculeLabel: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#1e40af',
    letterSpacing: '0.5px',
  },
  formGroup: { marginBottom: '12px' },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: '15px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    background: '#f9fafb',
    color: '#111827',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '12px 14px',
    fontSize: '15px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    background: '#f9fafb',
    color: '#111827',
    boxSizing: 'border-box',
  },
  searchInput: {
    width: '100%',
    padding: '12px 14px',
    fontSize: '15px',
    border: '2px solid #3b82f6',
    borderRadius: '12px',
    background: 'white',
    color: '#111827',
    boxSizing: 'border-box',
  },
  searchResults: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: 'white',
    border: '2px solid #3b82f6',
    borderTop: 'none',
    borderRadius: '0 0 12px 12px',
    maxHeight: '220px',
    overflowY: 'auto',
    zIndex: 30,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  searchResultItem: {
    padding: '10px 14px',
    cursor: 'pointer',
    borderBottom: '1px solid #e5e7eb',
  },
  searchResultName: {
    fontWeight: '600',
    color: '#111827',
    fontSize: '14px',
  },
  searchResultCompany: { fontSize: '12px', color: '#64748b', marginTop: '2px' },
  primaryBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  ghostBtn: {
    width: '100%',
    padding: '12px',
    background: 'white',
    border: '2px dashed #3b82f6',
    borderRadius: '12px',
    color: '#1e40af',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  amberBtn: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  btnRow: { display: 'flex', gap: '8px' },
  etapeCard: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '12px',
    marginBottom: '10px',
  },
  etapeRetourCard: {
    background: '#fef3c7',
    border: '1px solid #f59e0b',
    borderRadius: '12px',
    padding: '12px',
    marginBottom: '10px',
  },
  etapeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  badge: {
    fontSize: '11px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '8px',
    background: '#dbeafe',
    color: '#1e40af',
  },
  badgeRetour: {
    fontSize: '11px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '8px',
    background: '#fde68a',
    color: '#92400e',
  },
  etapeName: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: '4px',
  },
  etapeAddr: {
    fontSize: '12px',
    color: '#64748b',
    marginBottom: '2px',
    wordBreak: 'break-word',
  },
  etapeKmRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '6px',
    gap: '8px',
  },
  kmInput: {
    width: '90px',
    padding: '6px 10px',
    fontSize: '14px',
    fontWeight: '700',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    textAlign: 'right',
    color: '#0f172a',
  },
  removeBtn: {
    background: '#fee2e2',
    border: 'none',
    color: '#b91c1c',
    padding: '6px 10px',
    borderRadius: '8px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '12px',
  },
  recapBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'white',
    borderTop: '1px solid #e5e7eb',
    padding: '12px 16px',
    boxShadow: '0 -4px 12px rgba(0,0,0,0.06)',
    zIndex: 50,
  },
  recapRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  recapLabel: { fontSize: '12px', color: '#64748b' },
  recapValue: { fontSize: '15px', fontWeight: '700', color: '#0f172a' },
  recapMontant: { fontSize: '20px', fontWeight: '900', color: '#1e40af' },
  saveBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
  },
  saveBtnDisabled: {
    width: '100%',
    padding: '14px',
    background: '#cbd5e1',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'not-allowed',
  },
  homeChip: {
    background: '#f1f5f9',
    borderRadius: '10px',
    padding: '10px 12px',
    marginBottom: '10px',
    fontSize: '12px',
    color: '#334155',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  warning: {
    background: '#fef3c7',
    border: '1px solid #f59e0b',
    borderRadius: '8px',
    padding: '10px 12px',
    color: '#92400e',
    fontSize: '12px',
    marginTop: '8px',
  },
};

function CalculateurKilometrique({ onBack, onSave }) {
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  const [inspecteurId, setInspecteurId] = useState(null);
  const [inspecteurNom, setInspecteurNom] = useState('');
  const [adressePersonnelle, setAdressePersonnelle] = useState('');
  const [vehiculeInfo, setVehiculeInfo] = useState(null);
  const [baremesFromApi, setBaremesFromApi] = useState([]);

  const [adresseSiege, setAdresseSiege] = useState('');
  // Réglage entreprise ("unipersonnel" = auto-validation, "multi_employes" = soumission
  // pour validation par un admin) — mêmes valeurs que côté web (Parametres.jsx).
  const [modeGestionFrais, setModeGestionFrais] = useState('unipersonnel');

  const [clients, setClients] = useState([]);
  const [etablissements, setEtablissements] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  const [dateTournee, setDateTournee] = useState(todayIso());
  const [pointDepart, setPointDepart] = useState('');
  const [etapes, setEtapes] = useState([]); // [{ id, type, origine, destination, km, duree_text, client_id, client_nom, etablissement_id, etablissement_nom }]

  // Formulaire d'ajout d'étape (inline, pas de modal pour mobile)
  const [showAddStep, setShowAddStep] = useState(false);
  const [pendingClientId, setPendingClientId] = useState('');
  const [pendingClientNom, setPendingClientNom] = useState('');
  const [pendingEtablissementId, setPendingEtablissementId] = useState('');
  const [pendingEtablissementNom, setPendingEtablissementNom] = useState('');
  const [pendingDestination, setPendingDestination] = useState('');

  // Origine pour la prochaine étape : dernière destination, sinon point de départ
  const origineProchaineEtape = useMemo(() => {
    if (etapes.length === 0) return pointDepart;
    return etapes[etapes.length - 1].destination || pointDepart;
  }, [etapes, pointDepart]);

  const kmTotal = useMemo(
    () => etapes.reduce((acc, e) => acc + (Number(e.km) || 0), 0),
    [etapes]
  );

  const resolveBaremeTaux = (puissanceKey) => {
    const match = baremesFromApi.find(
      (b) =>
        normalizePuissanceFiscaleKey(b.puissance_vehicule) === puissanceKey &&
        b.actif !== false
    );
    if (match) {
      return {
        t1: Number(match.tranche_0_5000),
        t2: Number(match.tranche_5001_20000),
        t3: Number(match.tranche_plus_20000),
      };
    }
    const fallback = BAREMES_2026[puissanceKey] || BAREMES_2026['5cv'];
    return { t1: fallback.t1, t2: fallback.t2, t3: fallback.t3 };
  };

  // Calcul d'un montant simple : un seul taux selon le km annuel estimé
  // (aligné sur l'app principale qui n'applique plus la logique multi-tranches sur 1 trajet)
  const calculMontant = useMemo(() => {
    if (!vehiculeInfo?.puissance_fiscale || kmTotal <= 0) return null;
    const bareme = resolveBaremeTaux(vehiculeInfo.puissance_fiscale);
    const kmAnnuel = vehiculeInfo.kilometrage_annuel_estime || 0;
    let taux, tranche;
    if (kmAnnuel <= 5000) {
      taux = bareme.t1;
      tranche = '0 - 5 000 km/an';
    } else if (kmAnnuel <= 20000) {
      taux = bareme.t2;
      tranche = '5 001 - 20 000 km/an';
    } else {
      taux = bareme.t3;
      tranche = '+ 20 000 km/an';
    }
    return {
      taux,
      tranche,
      montantTotal: kmTotal * taux,
      puissance: vehiculeInfo.puissance_fiscale,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehiculeInfo, kmTotal, baremesFromApi]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Clients (pour la recherche dans l'ajout d'étape)
      const clientsResponse = await apiService.getClientsForFrais();
      if (clientsResponse.success && clientsResponse.clients) {
        setClients(clientsResponse.clients.map(normalizeClientFromApi));
      }

      // 2. Inspecteur authentifié (clone)
      const clone = cloneManager.getClone();
      const inspId = clone?.inspecteur?.id;
      const inspNom = clone?.inspecteur
        ? `${clone.inspecteur.prenom} ${clone.inspecteur.nom}`.trim()
        : 'Inspecteur';
      setInspecteurId(inspId);
      setInspecteurNom(inspNom);

      // 3. Infos véhicule + adresse personnelle
      let inspecteurApi = null;
      if (inspId) {
        const vehiculeResponse = await apiService.getInspecteurVehiculeInfo(inspId);
        if (vehiculeResponse.success) {
          if (vehiculeResponse.vehicule) {
            setVehiculeInfo(vehiculeResponse.vehicule);
          }
          inspecteurApi = vehiculeResponse.inspecteur;
          if (inspecteurApi?.adresse_personnelle) {
            setAdressePersonnelle(inspecteurApi.adresse_personnelle);
          }
        }
      }

      // 4. Adresse du siège + mode de gestion des frais (paramètres entreprise)
      let siege = '';
      try {
        const params = await apiService.getParametres();
        const list = Array.isArray(params?.parametres) ? params.parametres : [];
        siege = list[0]?.headquarters_address || '';
        setModeGestionFrais(list[0]?.mode_gestion_frais || 'unipersonnel');
      } catch (e) {
        // ignore : on a un fallback
      }
      const siegeFinal = siege || ADRESSE_SIEGE_FALLBACK;
      setAdresseSiege(siegeFinal);

      // 5. Point de départ par défaut : adresse perso > siège
      const defaut = inspecteurApi?.adresse_personnelle || siegeFinal;
      setPointDepart(defaut);

      // 6. Barèmes
      const year = new Date().getFullYear();
      const br = await apiService.listBaremesKilometriques({ annee: year, actif: true });
      if (br.success && br.baremes?.length) {
        setBaremesFromApi(br.baremes);
      }
    } catch (error) {
      console.error('❌ Erreur chargement données tournée:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter((client) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return false;
    return (
      (client.nom || '').toLowerCase().includes(q) ||
      (client.prenom || '').toLowerCase().includes(q) ||
      (client.email || '').toLowerCase().includes(q) ||
      (client.entreprise || '').toLowerCase().includes(q) ||
      (client.ville || '').toLowerCase().includes(q) ||
      (client.raison_sociale || '').toLowerCase().includes(q)
    );
  });

  const handleClientSelectForStep = async (client) => {
    const label = clientDisplayName(client);
    setPendingClientId(client.id);
    setPendingClientNom(label);
    setSearchQuery(label);
    setShowSearchResults(false);

    const etablissementsResponse = await apiService.getClientEtablissements(client.id);
    const list = (etablissementsResponse.success && etablissementsResponse.etablissements
      ? etablissementsResponse.etablissements
      : []
    ).map(normalizeEtablissementFromApi);
    setEtablissements(list);

    const addrPrincipale = destinationFromClient(client);

    if (list.length === 1) {
      const etab = list[0];
      setPendingEtablissementId(etab.id);
      setPendingEtablissementNom(etab.nom || '');
      setPendingDestination(destinationFromEtablissement(etab) || addrPrincipale);
    } else {
      setPendingEtablissementId('');
      setPendingEtablissementNom('');
      setPendingDestination(addrPrincipale);
    }
  };

  const handleEtablissementSelectForStep = (etablissementId) => {
    const client = clients.find((x) => x.id === pendingClientId);
    const addrPrincipale = destinationFromClient(client);
    if (!etablissementId) {
      setPendingEtablissementId('');
      setPendingEtablissementNom('');
      setPendingDestination(addrPrincipale);
      return;
    }
    const etab = etablissements.find((e) => e.id === etablissementId);
    if (etab) {
      setPendingEtablissementId(etab.id);
      setPendingEtablissementNom(etab.nom || '');
      setPendingDestination(destinationFromEtablissement(etab) || addrPrincipale);
    }
  };

  const resetPendingForm = () => {
    setShowAddStep(false);
    setPendingClientId('');
    setPendingClientNom('');
    setPendingEtablissementId('');
    setPendingEtablissementNom('');
    setPendingDestination('');
    setSearchQuery('');
    setShowSearchResults(false);
    setEtablissements([]);
  };

  const handleAjouterEtape = async () => {
    if (!pointDepart) {
      alert('⚠️ Veuillez renseigner le point de départ.');
      return;
    }
    if (!pendingClientId) {
      alert('⚠️ Veuillez sélectionner un client.');
      return;
    }
    if (!pendingDestination) {
      alert('⚠️ Adresse de destination introuvable.');
      return;
    }

    setCalculating(true);
    try {
      const origine = origineProchaineEtape;
      const response = await apiService.calculateDistance(origine, pendingDestination);

      if (!response.success) {
        alert('❌ Erreur calcul: ' + (response.error || 'Erreur inconnue'));
        return;
      }

      setEtapes((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          type: 'visite',
          origine,
          destination: response.destination || pendingDestination,
          km: Number(response.distance_km) || 0,
          duree_text: response.duree_text || '',
          client_id: pendingClientId,
          client_nom: pendingClientNom,
          etablissement_id: pendingEtablissementId || null,
          etablissement_nom: pendingEtablissementNom || null,
        },
      ]);
      resetPendingForm();
    } catch (error) {
      console.error('❌ Erreur ajout étape:', error);
      alert('❌ Erreur lors du calcul: ' + error.message);
    } finally {
      setCalculating(false);
    }
  };

  const handleRetourDomicile = async () => {
    if (!pointDepart) {
      alert('⚠️ Point de départ non renseigné.');
      return;
    }
    if (etapes.length === 0) {
      alert('⚠️ Ajoutez au moins une étape avant le retour.');
      return;
    }
    setCalculating(true);
    try {
      const origine = origineProchaineEtape;
      const response = await apiService.calculateDistance(origine, pointDepart);
      if (!response.success) {
        alert('❌ Erreur calcul retour: ' + (response.error || 'Erreur inconnue'));
        return;
      }
      setEtapes((prev) => [
        ...prev,
        {
          id: `${Date.now()}-retour`,
          type: 'retour',
          origine,
          destination: response.destination || pointDepart,
          km: Number(response.distance_km) || 0,
          duree_text: response.duree_text || '',
          client_id: null,
          client_nom: 'Retour au domicile',
          etablissement_id: null,
          etablissement_nom: null,
        },
      ]);
    } catch (error) {
      console.error('❌ Erreur retour domicile:', error);
      alert('❌ Erreur lors du calcul: ' + error.message);
    } finally {
      setCalculating(false);
    }
  };

  const removeEtape = (id) => {
    setEtapes((prev) => prev.filter((e) => e.id !== id));
  };

  const updateEtapeKm = (id, value) => {
    const num = Number(value) || 0;
    setEtapes((prev) => prev.map((e) => (e.id === id ? { ...e, km: num } : e)));
  };

  const handleSave = async () => {
    if (!inspecteurId) {
      alert('⚠️ Inspecteur non identifié.');
      return;
    }
    if (etapes.length === 0) {
      alert('⚠️ Ajoutez au moins une étape à votre tournée.');
      return;
    }
    if (!calculMontant) {
      alert('⚠️ Aucun calcul disponible (vérifiez la puissance fiscale du véhicule).');
      return;
    }

    setSaving(true);
    try {
      const moisComptable = dateTournee.slice(0, 7);
      const description = `Tournée du ${dateTournee} - ${etapes.length} étape(s), ${kmTotal.toFixed(1)} km`;
      const trajetResume = [
        pointDepart || 'Départ',
        ...etapes.map((e) => e.client_nom || e.destination || '?'),
      ].join(' → ');

      const etapesPayload = etapes.map((e, idx) => ({
        ordre: idx + 1,
        type: e.type,
        origine: e.origine,
        destination: e.destination,
        km: e.km,
        duree_text: e.duree_text || null,
        client_id: e.client_id || null,
        client_nom: e.client_nom || null,
        etablissement_id: e.etablissement_id || null,
        etablissement_nom: e.etablissement_nom || null,
      }));

      const dernier = etapes[etapes.length - 1];
      const pointArriveeFinal = dernier?.type === 'retour' ? pointDepart : dernier?.destination || '';
      const premiereVisite = etapes.find((e) => e.type !== 'retour');

      const noteData = {
        inspecteur_id: inspecteurId,
        inspecteur_nom: inspecteurNom,
        date_depense: dateTournee,
        categorie: 'deplacement_vehicule',
        description,
        montant: Number(calculMontant.montantTotal.toFixed(2)),
        km_parcourus: Number(kmTotal.toFixed(1)),
        puissance_vehicule: calculMontant.puissance,
        trajet: trajetResume,
        client_visite: premiereVisite?.client_nom || null,
        etablissement_visite: premiereVisite?.etablissement_nom || null,
        mois_comptable: moisComptable,
        // "unipersonnel" -> auto-validée ; "multi_employes" -> soumise pour validation par
        // un admin (sinon la tournée resterait invisible dans la file de validation).
        statut: modeGestionFrais === 'unipersonnel' ? 'validee' : 'soumise',
        point_depart: pointDepart || null,
        point_arrivee: pointArriveeFinal || null,
        etapes: etapesPayload,
      };

      const response = await apiService.createNoteDeFraisKilometrique(noteData);
      if (response.success) {
        alert(`✅ Tournée enregistrée !\n${etapes.length} étape(s) — ${kmTotal.toFixed(1)} km — ${calculMontant.montantTotal.toFixed(2)} €`);
        if (onSave) onSave();
      } else {
        alert('❌ Erreur: ' + (response.error || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('❌ Erreur sauvegarde tournée:', error);
      alert('❌ Erreur: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>← Retour</button>
        <h1 style={styles.headerTitle}>Tournée km</h1>
        <div style={{ width: '70px' }}></div>
      </div>

      <div style={styles.content}>
        {/* Bloc véhicule */}
        {vehiculeInfo && (
          <div style={styles.vehiculeCard}>
            <div style={styles.vehiculeLabel}>VÉHICULE</div>
            <div style={{ fontSize: '14px', fontWeight: '700', marginTop: '2px' }}>
              {vehiculeInfo.marque} {vehiculeInfo.modele}
            </div>
            <div style={{ fontSize: '12px', color: '#1e40af' }}>
              {vehiculeInfo.immatriculation} • {vehiculeInfo.puissance_fiscale}
            </div>
            <div style={{ fontSize: '11px', color: '#1e40af', marginTop: '6px' }}>
              📊 Annuel estimé : <strong>{vehiculeInfo.kilometrage_annuel_estime.toLocaleString('fr-FR')} km</strong>
              {' • '}
              📈 Utilisé : <strong>{vehiculeInfo.kilometrage_utilise_annuel.toLocaleString('fr-FR')} km</strong>
            </div>
          </div>
        )}

        {/* Paramètres tournée */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📅 Paramètres de la tournée</h3>

          <div style={styles.formGroup}>
            <label style={styles.label}>Date</label>
            <input
              type="date"
              style={styles.input}
              value={dateTournee}
              onChange={(e) => setDateTournee(e.target.value)}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>🏠 Point de départ (domicile)</label>
            <input
              type="text"
              style={styles.input}
              value={pointDepart}
              onChange={(e) => setPointDepart(e.target.value)}
              placeholder="Ex: 12 rue de la République, 87000 Limoges"
            />
            {!adressePersonnelle && (
              <div style={styles.warning}>
                ⚠️ Aucune adresse personnelle enregistrée pour vous. Le siège est utilisé par défaut.
                Pour ne plus avoir à saisir, demandez à votre administrateur de compléter votre fiche
                inspecteur.
              </div>
            )}
          </div>
        </div>

        {/* Liste des étapes */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>🛣️ Étapes ({etapes.length})</h3>

          {/* Récap départ */}
          <div style={styles.homeChip}>
            🏠 <strong>Départ :</strong> {pointDepart || '—'}
          </div>

          {etapes.map((etape, idx) => (
            <div
              key={etape.id}
              style={etape.type === 'retour' ? styles.etapeRetourCard : styles.etapeCard}
            >
              <div style={styles.etapeHeader}>
                <span style={etape.type === 'retour' ? styles.badgeRetour : styles.badge}>
                  {etape.type === 'retour' ? `🏠 Retour` : `Étape ${idx + 1}`}
                </span>
                <button
                  style={styles.removeBtn}
                  onClick={() => removeEtape(etape.id)}
                >
                  Supprimer
                </button>
              </div>
              <div style={styles.etapeName}>{etape.client_nom || '—'}</div>
              {etape.etablissement_nom && (
                <div style={styles.etapeAddr}>📍 {etape.etablissement_nom}</div>
              )}
              <div style={styles.etapeAddr}>
                <strong>De :</strong> {etape.origine}
              </div>
              <div style={styles.etapeAddr}>
                <strong>À :</strong> {etape.destination}
              </div>
              <div style={styles.etapeKmRow}>
                {etape.duree_text ? (
                  <span style={{ fontSize: '12px', color: '#64748b' }}>⏱️ {etape.duree_text}</span>
                ) : <span />}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="number"
                    step="0.1"
                    style={styles.kmInput}
                    value={etape.km}
                    onChange={(e) => updateEtapeKm(etape.id, e.target.value)}
                  />
                  <span style={{ fontSize: '13px', fontWeight: '700' }}>km</span>
                </div>
              </div>
            </div>
          ))}

          {/* Formulaire d'ajout d'étape (inline) */}
          {showAddStep ? (
            <div style={{ ...styles.etapeCard, borderColor: '#3b82f6', borderWidth: '2px' }}>
              <h4 style={{ ...styles.cardTitle, fontSize: '14px', marginBottom: '10px' }}>
                ➕ Nouveau client
              </h4>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '10px' }}>
                Départ depuis : <strong>{origineProchaineEtape || '—'}</strong>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Client</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    style={styles.searchInput}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSearchResults(e.target.value.length > 0);
                    }}
                    placeholder="Rechercher un client..."
                  />
                  {showSearchResults && filteredClients.length > 0 && (
                    <div style={styles.searchResults}>
                      {filteredClients.slice(0, 30).map((client) => (
                        <div
                          key={client.id}
                          style={styles.searchResultItem}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleClientSelectForStep(client);
                          }}
                        >
                          <div style={styles.searchResultName}>
                            {clientDisplayName(client)}
                          </div>
                          {client.ville && (
                            <div style={styles.searchResultCompany}>{client.ville}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {pendingClientId && etablissements.length > 0 && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Établissement</label>
                  <select
                    style={styles.select}
                    value={pendingEtablissementId}
                    onChange={(e) => handleEtablissementSelectForStep(e.target.value)}
                  >
                    <option value="">Adresse principale du client</option>
                    {etablissements.map((etab) => (
                      <option key={etab.id} value={etab.id}>
                        {etab.nom} — {destinationFromEtablissement(etab) || ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {pendingDestination && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>🎯 Destination</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={pendingDestination}
                    onChange={(e) => setPendingDestination(e.target.value)}
                  />
                </div>
              )}

              <div style={styles.btnRow}>
                <button
                  style={{ ...styles.ghostBtn, flex: 1 }}
                  onClick={resetPendingForm}
                >
                  Annuler
                </button>
                <button
                  style={{ ...styles.primaryBtn, flex: 2 }}
                  onClick={handleAjouterEtape}
                  disabled={calculating || !pendingClientId || !pendingDestination}
                >
                  {calculating ? '⏳ Calcul…' : '✓ Ajouter'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
              <button
                style={styles.ghostBtn}
                onClick={() => setShowAddStep(true)}
                disabled={!pointDepart || !inspecteurId || calculating}
              >
                ➕ Ajouter une étape (client)
              </button>
              <button
                style={styles.amberBtn}
                onClick={handleRetourDomicile}
                disabled={etapes.length === 0 || !pointDepart || calculating}
              >
                🏠 Retour au domicile
              </button>
            </div>
          )}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
            ⏳ Chargement…
          </div>
        )}
      </div>

      {/* Barre récap fixe en bas */}
      <div style={styles.recapBar}>
        <div style={styles.recapRow}>
          <span style={styles.recapLabel}>{etapes.length} étape(s) — {kmTotal.toFixed(1)} km</span>
          <span style={styles.recapMontant}>
            {calculMontant ? `${calculMontant.montantTotal.toFixed(2)} €` : '—'}
          </span>
        </div>
        {calculMontant && (
          <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>
            {calculMontant.taux} €/km • {calculMontant.tranche} • {calculMontant.puissance}
          </div>
        )}
        <button
          style={
            !calculMontant || etapes.length === 0 || saving
              ? styles.saveBtnDisabled
              : styles.saveBtn
          }
          onClick={handleSave}
          disabled={!calculMontant || etapes.length === 0 || saving}
        >
          {saving ? '⏳ Enregistrement…' : '💾 Enregistrer la tournée'}
        </button>
      </div>
    </div>
  );
}

export default CalculateurKilometrique;
